import { Toaster } from "@/shared/components/ui/toaster";
import { Toaster as Sonner } from "@/shared/components/ui/sonner";
import { TooltipProvider } from "@/shared/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { setupGlobalErrorHandlers } from "@/core/api/errorLogger";
import { AuthProvider } from "@/core/auth/AuthProvider";
import { TenantProvider } from "@/contexts/TenantContext";
import { GlobalSearch } from "@/shared/components/GlobalSearch";
import { KeyboardShortcutsHelp } from "@/shared/components/KeyboardShortcutsHelp";
import { DrPhillipChatWrapper } from "@/features/dashboard/components/DrPhillipChatWrapper";
import { DrPhillipProvider } from "@/features/dashboard/context/DrPhillipContext";
import { ErrorBoundary } from "@/shared/components/ErrorBoundaries";
import { ThemeProvider } from "next-themes";
import { AppRoutes } from "@/app/routes/AppRoutes";

const CookieConsent = lazy(() =>
  import("@/shared/components/CookieConsent").then((m) => ({
    default: m.CookieConsent,
  }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
      refetchOnMount: "always",
    },
    mutations: {
      retry: 0,
    },
  },
});

const App = () => {
  useEffect(() => {
    const cleanup = setupGlobalErrorHandlers();
    return cleanup;
  }, []);

  return (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
          <TooltipProvider>
            <AuthProvider>
              <TenantProvider>
                <DrPhillipProvider>
                <GlobalSearch />
                <KeyboardShortcutsHelp />
                <Toaster />
                <Sonner />
                <DrPhillipChatWrapper />
                <Suspense fallback={null}>
                  <CookieConsent />
                </Suspense>
                <AppRoutes />
                </DrPhillipProvider>
              </TenantProvider>
            </AuthProvider>
          </TooltipProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
